
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

export interface CRMSettings {
  bulkInviteTime: string;
  maxDailyInvites: number;
  defaultTemplateId: string;
}

export const useCrmSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<CRMSettings>({
    bulkInviteTime: '15:00', // Default 3:00 PM
    maxDailyInvites: 1000,
    defaultTemplateId: '1', // Default template ID
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        // In a real app, we would fetch from an API or database
        // For now, we'll get from localStorage or use defaults
        const savedSettings = localStorage.getItem('crmSettings');
        if (savedSettings) {
          setSettings(JSON.parse(savedSettings));
        }
      } catch (error) {
        console.error('Error loading CRM settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load CRM settings',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [toast]);

  // Save settings
  const saveSettings = async (newSettings: Partial<CRMSettings>) => {
    setIsSaving(true);
    try {
      // Update settings
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      
      // In a real app, we would save to an API or database
      localStorage.setItem('crmSettings', JSON.stringify(updatedSettings));
      
      toast({
        title: 'Settings Saved',
        description: 'CRM settings have been updated successfully.',
      });
      
      return updatedSettings;
    } catch (error) {
      console.error('Error saving CRM settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save CRM settings',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    settings,
    isLoading,
    isSaving,
    saveSettings,
  };
};
